import React from 'react';
import {ConfigProvider, Dropdown, Tag, Tree} from 'antd';
import type {MenuProps} from 'antd';
import type {EventDataNode} from 'antd/es/tree';
import {
	ApartmentOutlined,
	DeleteOutlined,
	EditOutlined,
	FileOutlined,
	FolderAddOutlined,
	FolderOutlined,
	HolderOutlined,
	ImportOutlined,
	ShareAltOutlined,
	PlusOutlined,
	ProjectOutlined,
} from '@ant-design/icons';
import type {WorkspaceDataNode} from '@/types/workspace/tree.types';
import {canDropOn, resolveDropParent} from '@/utils/workspace/treeData';
import {
	canDeleteProject,
	canEditContent,
	canShare,
	canManageProject,
	roleLabel,
} from '@/utils/workspace/projectRole';
import './WorkspaceTree.css';

type Props = {
	treeData: WorkspaceDataNode[];
	loadedKeys: React.Key[];
	expandedKeys: React.Key[];
	selectedKeys: React.Key[];
	onLoadData: (node: EventDataNode<WorkspaceDataNode>) => Promise<void>;
	onLoadedKeysChange: (keys: React.Key[]) => void;
	onExpand: (keys: React.Key[]) => void;
	onSelect: (keys: React.Key[]) => void;
	onOpenDiagram: (node: WorkspaceDataNode) => void;
	onAddFolder: () => void;
	onAddDiagram: () => void;
	onImport: () => void;
	onEdit: () => void;
	onDelete: () => void;
	onShare: (node: WorkspaceDataNode) => void;
	onMove: (dragNode: WorkspaceDataNode, destParent: WorkspaceDataNode) => void;
};

function nodeIcon(type: WorkspaceDataNode['nodeType']) {
	if (type === 'project') return <ProjectOutlined />;
	if (type === 'folder') return <FolderOutlined />;
	return <ApartmentOutlined />;
}

const WorkspaceTree: React.FC<Props> = ({
	treeData,
	loadedKeys,
	expandedKeys,
	selectedKeys,
	onLoadData,
	onLoadedKeysChange,
	onExpand,
	onSelect,
	onOpenDiagram,
	onAddFolder,
	onAddDiagram,
	onImport,
	onEdit,
	onDelete,
	onShare,
	onMove,
}) => {
	const [contextMenu, setContextMenu] = React.useState<{
		x: number;
		y: number;
		node: WorkspaceDataNode;
	} | null>(null);

	const contextItems: MenuProps['items'] = React.useMemo(() => {
		const node = contextMenu?.node;
		if (!node) return [];
		const editable = canEditContent(node.role);
		if (node.nodeType === 'diagram') {
			const items: MenuProps['items'] = [{key: 'open', icon: <FileOutlined />, label: 'Открыть'}];
			if (editable) {
				items.push(
					{key: 'edit', icon: <EditOutlined />, label: 'Редактировать диаграмму'},
					{key: 'delete', icon: <DeleteOutlined />, danger: true, label: 'Удалить диаграмму'}
				);
			}
			return items;
		}
		if (!editable) return [];
		const addItems = [
			{key: 'add-folder', icon: <FolderAddOutlined />, label: 'Добавить папку'},
			{key: 'add-diagram', icon: <PlusOutlined />, label: 'Добавить диаграмму'},
			{key: 'import', icon: <ImportOutlined />, label: 'Импортировать диаграмму'},
		];
		if (node.nodeType === 'project') {
			const items = [...addItems];
			if (canShare(node.role)) {
				items.push({key: 'share', icon: <ShareAltOutlined />, label: 'Поделиться'});
			}
			if (canManageProject(node.role)) {
				items.push({key: 'edit', icon: <EditOutlined />, label: 'Редактировать проект'});
			}
			if (canDeleteProject(node.role)) {
				items.push({key: 'delete', icon: <DeleteOutlined />, danger: true, label: 'Удалить проект'});
			}
			return items;
		}
		return [
			...addItems,
			{key: 'edit', icon: <EditOutlined />, label: 'Редактировать папку'},
			{key: 'delete', icon: <DeleteOutlined />, danger: true, label: 'Удалить папку'},
		];
	}, [contextMenu]);

	const handleMenuClick: MenuProps['onClick'] = ({key}) => {
		const node = contextMenu?.node;
		setContextMenu(null);
		if (!node) return;
		if (key === 'open') onOpenDiagram(node);
		if (key === 'add-folder') onAddFolder();
		if (key === 'add-diagram') onAddDiagram();
		if (key === 'import') onImport();
		if (key === 'edit') onEdit();
		if (key === 'share') onShare(node);
		if (key === 'delete') onDelete();
	};

	const treeWrapRef = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		const selected = treeWrapRef.current?.querySelector('.ant-tree-treenode-selected');
		selected?.scrollIntoView({block: 'nearest'});
	}, [selectedKeys]);

	return (
		<>
			<ConfigProvider theme={{components: {Tree: {titleHeight: 32}}}}>
			<div className="workspace-tree" ref={treeWrapRef}>
			<Tree<WorkspaceDataNode>
				blockNode
				draggable={{
					icon: <HolderOutlined />,
					nodeDraggable: node => node.nodeType !== 'project' && canEditContent(node.role),
				}}
				allowDrop={({dragNode, dropNode, dropPosition}) =>
					canDropOn(dragNode, dropNode, dropPosition)
				}
				onDrop={info => {
					const dest = resolveDropParent(
						treeData,
						info.dragNode,
						info.node,
						info.dropToGap
					);
					if (dest) onMove(info.dragNode, dest);
				}}
				loadData={onLoadData}
				treeData={treeData}
				loadedKeys={loadedKeys}
				onLoad={onLoadedKeysChange}
				expandedKeys={expandedKeys}
				onExpand={onExpand}
				selectedKeys={selectedKeys}
				onSelect={onSelect}
				onDoubleClick={(_e, node) => {
					if (node.nodeType === 'diagram') onOpenDiagram(node);
				}}
				onRightClick={({event, node}) => {
					event.preventDefault();
					onSelect([node.key]);
					setContextMenu({x: event.clientX, y: event.clientY, node});
				}}
				titleRender={node => (
					<span className="workspace-tree-node">
						{nodeIcon(node.nodeType)}
						<span className="workspace-tree-node-title">{String(node.title ?? '')}</span>
						{node.shared && node.role ? <Tag>{roleLabel(node.role)}</Tag> : null}
					</span>
				)}
			/>
			</div>
			</ConfigProvider>
			{contextMenu ? (
				<Dropdown
					open
					trigger={['click']}
					menu={{items: contextItems, onClick: handleMenuClick}}
					onOpenChange={open => {
						if (!open) setContextMenu(null);
					}}
				>
					<div
						style={{
							position: 'fixed',
							left: contextMenu.x,
							top: contextMenu.y,
							width: 1,
							height: 1,
						}}
					/>
				</Dropdown>
			) : null}
		</>
	);
};

export default WorkspaceTree;
